// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import {
  AssetLocationReportRow,
  PresenceReportResponse,
} from '@/report/api/types';
import { toPresenceBits } from '@/report/utilities/to-presence-bits';

export type LocationReportType = 'inventory' | 'field';

const PATHS: Record<LocationReportType, string> = {
  inventory: '/v1/report/asset/inventory',
  field: '/v1/report/asset/on-site',
};

interface Parameters {
  start: string;
  end?: string;
}

interface Response {
  data: PresenceReportResponse<AssetLocationReportRow>;
}

// The base64 mask is dropped once decoded so each row holds one representation, not two.
export interface DecodedAssetLocationReportRow
  extends Omit<AssetLocationReportRow, 'presenceBitMask'> {
  presenceBits: Uint8Array;
}

/**
 * Requests location report of assets for a given time period.
 *
 * @param type - The type of the report.
 * @param parameters.start - The start of the time period.
 * @param parameters.end - The end of the time period.
 */
export async function getAssetLocationReport(
  type: LocationReportType,
  parameters: Parameters
) {
  const path = PATHS[type];

  // This API response is expected to be large, so the response is not parsed
  // for performance reasons. Instead, the response type is assumed to be
  // correct and the data is returned directly.
  const { data } = await api.get<Response>(path, { params: parameters });

  // Decode once per row here, never inside a column accessor, which runs per row per column.
  const rows = data.data.data.map(({ presenceBitMask, ...row }) => ({
    ...row,
    presenceBits: toPresenceBits(presenceBitMask),
  }));

  return { startOn: data.data.startOn, dayCount: data.data.dayCount, rows };
}
